import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { 
  HeroSection,
  FeaturesSection,
  HowItWorksSection,
  // Optional sections - uncomment to add:
  // FAQSection,
  // PricingSection, 
  // TestimonialsSection
} from "@/components/landing";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

if (import.meta.env.DEV) {
  console.log('🔍 LandingPage module loading...');

  // Test individual component imports
  try {
    console.log('🔍 Testing Navigation import...');
    const nav = Navigation;
    console.log('✅ Navigation imported successfully');
  } catch (error) {
    console.error('❌ Navigation import error:', error);
  }

  try {
    console.log('🔍 Testing Footer import...');
    const footer = Footer;
    console.log('✅ Footer imported successfully');
  } catch (error) {
    console.error('❌ Footer import error:', error);
  }

  try {
    console.log('🔍 Testing landing components import...');
    const hero = HeroSection;
    const features = FeaturesSection;
    const howItWorks = HowItWorksSection;
    console.log('✅ Landing components imported successfully');
  } catch (error) {
    console.error('❌ Landing components import error:', error);
  }
}

/**
 * Landing Page Component
 * 
 * This is the main landing page with a clean, modular structure.
 * 
 * Core sections (always included):
 * - HeroSection: Main hero with CTA
 * - FeaturesSection: Key product features 
 * - HowItWorksSection: 3-step process
 * - StatsSection: Trust indicators
 * - CTASection: Final conversion section
 * 
 * Optional sections (import and add as needed):
 * - FAQSection: Frequently asked questions
 * - PricingSection: Pricing plans  
 * - TestimonialsSection: Customer testimonials
 */

const LandingPage = () => {
  if (import.meta.env.DEV) {
    console.log('🔍 LandingPage component rendering...');
  }
  
  try {
    if (import.meta.env.DEV) {
      console.log('🔍 About to call useAuth...');
    }
    const { user } = useAuth();
    if (import.meta.env.DEV) {
      console.log('✅ useAuth called successfully, user:', !!user);
    }
    
    const navigate = useNavigate();

    // Redirect authenticated users to dashboard
    useEffect(() => {
      if (import.meta.env.DEV) {
        console.log('🔍 useEffect triggered, user exists:', !!user);
      }
      if (user) {
        if (import.meta.env.DEV) {
          console.log('🔍 Redirecting to dashboard...');
        }
        navigate('/tracker/log');
      }
    }, [user, navigate]);

    if (import.meta.env.DEV) {
      console.log('🔍 About to render LandingPage JSX...');
    }
    
    // Simple fallback content first
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">Welcome to Activity Tracker</h1>
          <p className="text-muted-foreground">Your companion for achieving goals</p>
          <div className="space-x-4">
            <button 
              onClick={() => navigate('/auth')} 
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('❌ Error in LandingPage component:', error);
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Error Loading Page</h1>
          <p className="text-muted-foreground">Please check the console for details</p>
        </div>
      </div>
    );
  }
};

export default LandingPage;